export interface QuestionAnswerHistoryRecord {
    historyId: number;
    active: boolean;
    answerText: string;
    answerTone: 'green' | 'red' | 'amber' | 'default';
    dateTimeStamp: string;
    formattedTime: string;
    modifiedUserFullName: string;
    modifiedUserInitials: string;
    previousValue: string;
    newValue: string;
    dateObj: Date;
}

export interface QuestionAuditHistoryGroup {
    questionId: number;
    questionText: string;
    history: QuestionAnswerHistoryRecord[];
}

export interface AuditContributor {
    name: string;
    initials: string;
    entriesCount: number;
}

export interface ProcessedAuditTrailData {
    groups: QuestionAuditHistoryGroup[];
    questionHistoryMap: Map<number, QuestionAnswerHistoryRecord[]>;
    contributors: AuditContributor[];
    totalEntriesCount: number;
    uniqueQuestionsCount: number;
}

export function parseAuditTrailApiResponse(apiData: any[]): ProcessedAuditTrailData {
    if (!Array.isArray(apiData)) {
        return {
            groups: [],
            questionHistoryMap: new Map(),
            contributors: [],
            totalEntriesCount: 0,
            uniqueQuestionsCount: 0,
        };
    }

    const groups: QuestionAuditHistoryGroup[] = [];
    const questionHistoryMap = new Map<number, QuestionAnswerHistoryRecord[]>();
    const contributorCountsMap = new Map<string, number>();
    let totalEntriesCount = 0;

    apiData.forEach((qGroup: any) => {
        if (!qGroup || !qGroup.questionId) return;
        const qId = Number(qGroup.questionId);
        const qText = qGroup.questionText || 'Question';
        const rawHistory = Array.isArray(qGroup.history) ? qGroup.history : [];

        const sortedRawHistory = [...rawHistory].sort((a, b) => getHistoryTimestamp(a) - getHistoryTimestamp(b));

        const parsedRecords: QuestionAnswerHistoryRecord[] = [];
        let previousAnswerValue = 'First entry';

        sortedRawHistory.forEach((item: any) => {
            const historyId = item.opportunity_SRChecklist_Response_History_Id || item.historyId || 0;
            const active = !!item.active;
            const answersArr = Array.isArray(item.answers) ? item.answers : [];

            if (answersArr.length === 0) return;

            const textParts = answersArr
                .map((ans: any) => ans.answerText || ans.responseValue || ans.value || '')
                .filter(Boolean);
            const answerText = textParts.join(', ');
            const firstAns = answersArr[0];
            const toneStr = String(firstAns.answerTone || firstAns.tone || '').toLowerCase();
            const timestamp = firstAns.dateTimeStamp || firstAns.timestamp || new Date().toISOString();
            const userName = firstAns.modifiedUserFullName || firstAns.userName || '';
            const initials = firstAns.modifiedUserInitials || getInitials(userName);
            const dateObj = new Date(timestamp);
            const newValue = answerText || 'No answer';

            parsedRecords.push({
                historyId,
                active,
                answerText: newValue,
                answerTone: normalizeTone(toneStr),
                dateTimeStamp: timestamp,
                formattedTime: formatTimestamp(dateObj),
                modifiedUserFullName: userName,
                modifiedUserInitials: initials,
                previousValue: previousAnswerValue,
                newValue,
                dateObj,
            });

            previousAnswerValue = newValue;
            contributorCountsMap.set(userName, (contributorCountsMap.get(userName) || 0) + 1);
            totalEntriesCount++;
        });

        const displayRecords = [...parsedRecords].reverse();
        if (displayRecords.length > 0) {
            groups.push({ questionId: qId, questionText: qText, history: displayRecords });
            questionHistoryMap.set(qId, displayRecords);
        }
    });

    groups.sort((a, b) => {
        const timeA = a.history.length > 0 ? a.history[0].dateObj?.getTime() : 0;
        const timeB = b.history.length > 0 ? b.history[0].dateObj?.getTime() : 0;
        return timeB - timeA;
    });

    const contributors: AuditContributor[] = Array.from(contributorCountsMap.entries()).map(([name, count]) => ({
        name,
        initials: getInitials(name),
        entriesCount: count,
    }));

    return {
        groups,
        questionHistoryMap,
        contributors,
        totalEntriesCount,
        uniqueQuestionsCount: groups.length,
    };
}

function getHistoryTimestamp(item: any): number {
    if (!item) return 0;
    const answersArr = Array.isArray(item.answers) ? item.answers : [];
    if (answersArr.length > 0) {
        const ts = answersArr[0].dateTimeStamp;
        if (ts) return new Date(ts).getTime();
    }
    if (item.dateTimeStamp) return new Date(item.dateTimeStamp).getTime();
    return 0;
}

function normalizeTone(toneStr: string): 'green' | 'red' | 'amber' | 'default' {
    if (!toneStr) return 'default';
    if (toneStr.includes('red') || toneStr.includes('critical')) return 'red';
    if (toneStr.includes('amber') || toneStr.includes('yellow')) return 'amber';
    if (toneStr.includes('green') || toneStr.includes('success')) return 'green';
    return 'default';
}

export function getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
}

function formatTimestamp(date: Date): string {
    if (isNaN(date.getTime())) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${hours}:${minutes}:${seconds} ${ampm} - ${month} ${day}, ${year}`;
}
